export type Language = 'en' | 'nl';

export const translations = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      methodology: 'Methodology',
      about: 'About',
      contact: 'Contact',
    },
    hero: {
      title: 'Met Maät',
      slogan: 'Software Architecture & Technical Leadership',
      subtitle: 'You know your business, we know software architecture and development. Together we implement the right solution.',
      readMore: 'Read more',
      ctaPrimary: 'Our Services',
      ctaSecondary: 'Get in Touch',
    },
    homeFeatures: {
      title: 'Why choose us?',
      points: [
        'We always implement the right balance between necessary and desirable investments with a focus on efficiency, quality, and future-proofing.',
        'We structure, innovate, and increase quality and productivity in your software development and give direction to teams.',
        'Everything based on your requirements and our practical experience.',
      ]
    },
    notFound: {
      title: 'Page not found',
      subtitle: 'This page doesn\'t exist (anymore).',
      cta: 'Back to home',
    },
    experience: {
      title: 'Experience',
      subtitle: 'Over 14 years of building and guiding software at leading organizations.',
      caption: 'Trusted expertise at:',
    },

    recovery: {
      tag: 'Highlight',
      title: 'Project Recovery',
      desc: 'Dealing with exploding cloud costs or a team stuck in technical debt? We analyze the bottlenecks and restore the course with a concrete plan.',
      cta: 'Discover how we help',
    },
    aiSection: {
      title: 'Have you Met Maät?',
      intro: 'Our name is Dutch, and roughly translated it means acting with measure: the right balance between too much and too little. The name also nods to ancient Egypt, where Maät stood for order. That\'s exactly how we look at software development: enough structure to stay in control, enough speed to deliver value at minimum cost.',
      servicesLead: 'What that looks like in practice:',
      highlights: [
        'Software architecture',
        'AI adoption in your development process',
        'Technical leadership & team guidance',
        'Project recovery & architecture simplification',
        'Backend & cloud development',
        'Quality, documentation & handover',
      ],
      servicesCta: 'All our services',
      quote: 'Good architecture facilitates speed and ensures lasting results.',
    },
    footer: {
      desc: 'We bring structure, quality, and direction to teams building software, hands-on and from experience.',
      navTitle: 'Navigation',
      contactTitle: 'Contact',
      contactText: 'Would you like to know more about our services?',
      tagline: 'Balance in an accelerating world.',
      rights: 'All rights reserved.',
    },
    company: {
      detailsTitle: 'Company details',
      kvkLabel: 'Chamber of Commerce (KVK)',
      vatLabel: 'VAT',
    },
    partners: {
      kicker: 'Collaboration & network',
      contact: 'Discuss your project',
      more: 'Explore our network',
      compact: {
        title: 'A strong network',
        description: 'Met Maät works with experienced architects and specialists for additional expertise, capacity and continuity.',
      },
      organisations: {
        softwareLeader: {
          name: 'Software Leader',
          logoAlt: 'Software Leader logo',
          contactLabel: 'Discuss architecture expertise through Met Maät',
          specialism: 'Software architecture & technical leadership',
          summary: 'Like-minded architects who can step in or take over the architecture role.',
          description: 'Through Software Leader, we work with experienced software architects who share our approach to architecture and technical leadership. They can review designs, provide additional capacity or take over the architecture role when needed.',
        },
        jellybean: {
          name: 'JellyBean Consultancy',
          logoAlt: 'JellyBean Consultancy logo',
          contactLabel: 'Discuss your Microsoft 365 project through Met Maät',
          specialism: 'Microsoft 365 & Power Platform',
          summary: 'Microsoft 365 and Power Platform: setup, automation and adoption.',
          description: 'For projects involving Microsoft 365, SharePoint, Teams and Power Platform, we work with JellyBean Consultancy. From a well-structured digital workplace to automating business processes.',
        },
        modernDay: {
          name: 'Modern Day',
          logoAlt: 'Modern Day logo',
          contactLabel: 'Discuss AI training and compliance through Met Maät',
          specialism: 'AI training & compliance',
          summary: 'Practical AI training for teams, with a focus on responsible use and compliance.',
          description: 'For training in AI, responsible use and compliance, we work with Modern Day. Practical guidance that helps people and teams use AI in their everyday work.',
        },
      },
      services: {
        title: 'Does your project need additional expertise?',
        description: 'We also help with questions around Microsoft 365, AI training or additional architecture capacity. Together with our partners, we explore the approach and expertise that fit your needs.',
        sharedVision: 'A shared approach to architecture',
        continuity: 'We coordinate that support and knowledge transfer with continuity in mind, so the project can keep moving in the same direction.',
      },
    },
    services: {
      title: 'Our',
      titleAccent: 'Services',
      subtitle: 'Direction, quality, and efficiency in software development.',
      focusTitle: 'Focus areas',
      techTitle: 'Technologies',
      architecture: {
        title: 'Architecture & Advisory',
        desc: 'We lead projects as architect, review existing architectures, or advise on the big technical decisions. Simple, direct, and focused on results.',
        points: [
          'Software architecture',
          'Detailed Design',
          'Technical decision making',
          'Documentation Landscape',
          'Quality & Documentation',
          'Handover & Knowledge sharing',
        ],
      },
      engineering: {
        title: 'Engineering & AI',
        desc: 'We build software according to high quality standards. Additionally, we stay on top of AI usage within your business and during development.',
        points: [
          'Backend & Cloud development',
          'AI Adoption',
          'DevOps & CI/CD',
        ],
        tech: 'Azure, .NET, C#, ASP.NET, SQL, Entity Framework, REST APIs, Azure DevOps, TypeScript, etc.',
      },
      coaching: {
        title: 'Architect & Team Guidance',
        desc: 'From a sparring partner for your (leadership) team, to coaching tech-leads and architects, to providing training and setting up a better development workflow.',
        points: [
          'Decision making and Efficiency',
          'Coaching and Guidance',
          'Business Analysis',
          'Development Process Optimization',
          'IDesign in Practice',
        ],
      },
      recovery: {
        title: 'Project Recovery',
        desc: 'Stalled projects, back on track. We simplify the architecture and restore the balance.',
        points: [
          'Architecture simplification',
          'Cloud cost review',
          'Development methodology improvement',
          'Recovery & Improvement plan',
          'Stabilization & Handover',
          'Root cause analysis',
          'Other recovery & transition work',
        ],
      },
    },
    methodology: {
      tag: '',
      title: 'Our',
      titleAccent: 'Methodology',
      subtitle: 'A structured engineering approach.',
      clickToEnlarge: 'Click to enlarge',
      stepLabel: 'Step',
      pillars: [
        {
          title: 'Engineering',
          tagline: 'An engineered process, not improvisation',
          desc: 'An engineer\'s mindset and a disciplined process: design, review, build, test, repeat. Predictable and repeatable, with quality built in at every step.',
        },
        {
          title: 'Simplicity',
          tagline: 'The simplest solution that works',
          desc: 'We cut complexity back to the essence, and make it visual so the whole team and the business actually understand it.',
        },
        {
          title: 'Future-proof (AI)',
          tagline: 'Ready for what comes next',
          desc: 'Whether you give your specs, architecture and design to a development team or to an AI, the more context you provide, the better the output.',
        },
      ],
      pains: {
        kicker: 'The problem',
        title: 'Sounds familiar?',
        categoryProject: 'Project & Business',
        categoryTech: 'Code & Tech',
        categoryTeam: 'Team & Knowledge',
        items: [
          'We\'re not sure when it\'ll actually be done.',
          'We have no idea what this project will end up costing.',
          'Our cloud bill keeps climbing, and nobody can explain why.',
          'It works, but every little change takes forever.',
          'What we build doesn\'t match what the business expected.',
          'Only Bob understands that part of the code, and Bob is on holiday.',
          'Nobody quite knows what the others are building.',
          'How ready are we really for what comes next?',
          'Everyone\'s doing something with AI. Are we?',
        ],
      },
      collab: {
        title: 'You know your business, we know software architecture.',
        text: 'Most of these problems start in the design, not the code. Together we build software that fits the business and keeps delivering value.',
        cta: 'Let\'s team up',
      },
      principlesKicker: 'Our mindset',
      principlesTitle: 'The mindset we bring',
      archTitle: 'IDesign Architecture in Practice',
      archDesc: 'A typical IDesign system is decomposed into horizontal layers: Clients, Logic (Managers and Engines), Access (ResourceAccess) and Resources, supported by a set of cross-cutting Utilities such as Security, Logging and the Service Bus. This volatility-based structure keeps responsibilities isolated and the system resilient to change.',
      archAlt: 'Example IDesign layered architecture diagram showing Client, Logic, Access and Resource layers with cross-cutting Utilities.',
      archCaption: 'Example of a volatility-based IDesign architecture.',
      idesign: {
        kicker: 'Our methodology',
        title: 'Engineering methodology',
        intro: 'A set of processes and building blocks we apply wherever we can. As a whole or in combinations. A few examples:',
        idesignBadge: 'IDesign',
        blocks: {
          businessAnalysis: {
            label: 'Business Analysis',
            tagline: 'Understand the domain',
            desc: 'We map the business processes and definitions in international UML standards. Just enough documentation to capture what the organisation really needs, no more.',
          },
          charterRoadmap: {
            label: 'Charter & Roadmap',
            tagline: 'Project and priorities',
            desc: 'What\'s in the project, and what isn\'t. We map the scope, the related projects and features, and the order to build them in, as one simple visual overview.',
          },
          systemDesign: {
            label: 'System Design',
            tagline: 'What are we building?',
            desc: 'Based on Juval Löwy\'s IDesign, we decompose the system into small components by volatility, not functionality. From your core business (use cases) to a clear, layered architecture that stays resilient to change.',
          },
          projectDesign: {
            label: 'Project Design',
            tagline: 'How will we build it?',
            desc: 'There are many roads to Rome, cheapest, fastest, in parallel, or via an MVP. We turn the architecture into a realistic plan, duration, cost and critical path, and validate it can actually be executed before you commit.',
          },
          detailedDesign: {
            label: 'Detailed Design',
            tagline: 'Down to the contracts',
            desc: 'Interfaces, class hierarchies and data contracts per component, all in one documentation landscape. Traceable from business process and requirement down to the smallest component, so functional and technical people alike see where something comes from and why.',
          },
          execution: {
            label: 'Execution',
            tagline: 'Build it right',
            desc: 'A software factory: build each component through the same predictable lifecycle of design, construction, review and test.',
          },
        },
        complete: {
          wholeTitle: 'A complete method',
          wholeText: 'Together, the blocks form one coherent, proven method for designing and delivering software.',
          modularTitle: 'Or exactly the part you need',
          modularText: 'Every block stands on its own. We bring in exactly the piece that fits your situation.',
        },
        captions: {
          businessProcess: 'Business process analysis',
          scenarios: 'Execution options: parallel, phased, MVP',
          projects: 'Roadmap across multiple projects',
          useCase: 'From use case to call chain',
          arch: 'A volatility-based layered architecture',
          plan: 'Planning and critical-path analysis',
          landscape: 'One connected documentation landscape',
          serviceLifecycle: 'The service development lifecycle',
          sequenceDiagram: 'A use case as a sequence diagram',
        },
      },
    },
    about: {
      tag: '',
      title: 'About',
      titleAccent: '',
      subtitle: 'The person behind Met Maät.',
      exp: 'Ben Degens, founder Met Maät',
      p1: 'Ben Degens is an experienced software architect from the Netherlands, specializing in backend development (.NET C#, Azure) and system architecture using the IDesign methodology.',
      p2: 'Throughout his 14+ year career, he has contributed to software projects across diverse organizations, from financial services to medical technology and from telecom to industrial manufacturing. He has experience with both greenfield projects and recovering stalled initiatives, and with methodologies like IDesign, Scrum, SAFe, and Waterfall.',
      p3: 'For many organizations, bridging the gap between what\'s needed and what gets built is a challenge. Ben\'s approach starts with active listening and understanding each organization\'s unique workflow. He uses that foundation to clarify requirements, design systems, and break down complexity into manageable components.',
      p4: 'He is convinced that his clients\' success is his own. You can expect him to actively think (and act!) alongside you, fully contributing to the success of your organization.',
      location: 'Netherlands',
      languages: 'Dutch & English',
      certification: 'Azure, .NET, C#',
      tags: ['System Architecture', 'DevOps & CI/CD', 'Microservices', 'API (management)', 'Databases, (No)SQL', 'AI & Data', 'UML, Diagrams, Docs', 'Project, Charter, Roadmap', 'Project Recovery', 'Technical Leadership', 'IDesign'],
      expertiseTitle: 'Expertise & Experience',
      skill1: {
        title: 'Education & Method',
        desc: 'Trained through IDesign\'s Architects Master Class and Project Design Master Class, in-depth courses rather than quick certificates, plus years of hands-on experience in IDesign-minded environments.',
      },
      skill2: {
        title: 'Technology & AI',
        desc: 'We have deep knowledge of C#, .NET, Azure, cloud, and backend development. We favor boring technology that works over hype-driven trends. But we don\'t look away from what\'s new: we use AI where we can to speed things up without losing oversight.',
      },
      skill3: {
        title: 'Leadership',
        desc: 'Extensive experience across startups and large organizations, with Agile, Scrum, Waterfall, and SAFe. Focus on strengthening teams, technical leadership, and creating workable processes where quality and speed can coexist.',
      },
      ctaTitle: 'Get in Touch',
      ctaDesc: 'Do you have a software project that is getting stuck, or do you want to make a strong start? Contact Ben directly.',
      emailBtn: 'Send email',
    },
    contact: {
      tag: '',
      title: 'Get in',
      titleAccent: 'Touch',
      subtitle: 'From brainstorming to getting a project moving, we\'re happy to help.',
      infoTitle: 'Contact Information',
      emailLabel: 'Email',
      emailValue: 'info@metmaat.com',
      mobileLabel: 'Mobile',
      call: 'Call',
      whatsapp: 'WhatsApp',
      linkedinLabel: 'LinkedIn',
      contactInformation: 'Contact Information',
      description: 'Feel free to reach out (without obligation) for more information or specific questions about our services.',
      whoIsBen: 'Who is Ben?',
    },
    legal: {
      disclaimerTitle: 'Disclaimer',
      disclaimerSubtitle: 'Legal terms and conditions regarding the use of this website.',
      privacyTitle: 'Privacy Statement',
      disclaimerText: "Met Maät takes great care to ensure that the content of this website is correct and up to date. However, Met Maät accepts no liability for the completeness and correctness of the information on this website, nor for the content of websites it links to. Met Maät may change the information on this website at any time without prior notice. This also applies to the services described.\n\nThe services, methods and examples described on this website are indicative and do not constitute an offer. The obligations of Met Maät are limited to what has been laid down in an agreement. The general terms and conditions of Met Maät apply to such agreements and are sent along with the quote.\n\nMet Maät uses AI systems as a tool in preparing texts and other content, including content on this website. All content is reviewed by Met Maät before publication; Met Maät remains responsible for it.\n\nThe intellectual property rights to the content of this website belong to Met Maät, with the exception of third-party logos and trademarks. It is not permitted to publish or copy the website or any part of it without the explicit written permission of Met Maät.\n\nYou are responsible for keeping the applications you use to visit this website free of viruses. To the extent permitted by law, Met Maät is not liable for any direct or indirect damage related to the use of this website or any linked website, in particular not for outages or the loss of programs or data in your IT systems.",
      privacyVersion: 'Version 1.1, October 2026',
      privacyGeneral: 'The private limited liability company Met Maät B.V. (hereinafter referred to as Met Maät) understands the importance of privacy and careful handling of personal data. As a specialist in software architecture, quality structure, and project recovery, Met Maät maintains a high standard for information security and transparency, both in its technical solutions and in its own business operations.',
      privacySections: [
        {
          "title": "Who is responsible?",
          "paragraphs": [
            "Met Maät B.V., located at Penderskoolhofweg 7, 6343RG Klimmen, the Netherlands (Chamber of Commerce 42138772), is the controller for the data needed for its own business operations and administration. Met Maät has not appointed a data protection officer; questions can be sent directly to info@metmaat.com.",
            "When Met Maät works as a consultant or architect within a client's systems, the client is the controller for the data processed there. Met Maät then acts as a processor and records this in a data processing agreement where required."
          ]
        },
        {
          "title": "What data does Met Maät process?",
          "paragraphs": [
            "Met Maät only processes data necessary for a professional collaboration:"
          ],
          "items": [
            "Contact by email, phone or LinkedIn: your name, contact details and the content of your message.",
            "Collaboration: name, role, company, business contact details and correspondence about the project, such as architecture choices, documentation and quotes.",
            "Invoicing: company name and address, Chamber of Commerce number, VAT number, contact person and bank details.",
            "Technical data: your IP address, which is needed to display the website and is briefly logged by the hosting provider."
          ],
          "after": [
            "Met Maät does not ask for special categories of personal data, such as data about health or religion. This website has no contact form."
          ]
        },
        {
          "title": "Purposes and legal bases",
          "items": [
            "Answering questions and planning an introduction: legitimate interest.",
            "Preparing quotes and carrying out engagements: performance of the contract.",
            "Invoicing and administration: legal obligation (tax record-keeping).",
            "Security and proper functioning of the website: legitimate interest.",
            "Mentioning your name or company as a reference or in a case study: consent, which you can withdraw at any time."
          ],
          "after": [
            "Met Maät does not sell data, send newsletters or advertising, or create profiles."
          ]
        },
        {
          "title": "Retention periods",
          "items": [
            "Questions that do not lead to a collaboration: 12 months after the last contact.",
            "Correspondence with clients: during the collaboration and up to 2 years afterwards.",
            "Quotes and agreements: during the collaboration and up to 5 years afterwards (the statutory limitation period).",
            "Invoices and administration: 7 years, in line with the tax retention obligation.",
            "Consent for a reference: until you withdraw it."
          ]
        },
        {
          "title": "Recipients and processors",
          "paragraphs": [
            "Met Maät only shares data when this is necessary for the service or required by law. The following parties receive or process data on behalf of Met Maät:"
          ],
          "items": [
            "Microsoft (Microsoft 365): email and document storage.",
            "Google (Firebase): hosting.",
            "Anthropic (Claude), OpenAI (ChatGPT / Codex) and Google (Gemini): AI assistants.",
            "Accountant: processing of the financial administration and tax returns.",
            "Freelancers and specialists engaged by Met Maät for an assignment: only the data they need for that assignment, under confidentiality."
          ],
          "after": [
            "These parties only process data for the service provided to Met Maät and are bound by confidentiality and security obligations. Met Maät has configured the AI systems so that the data entered is not used to train their models.",
            "Client data that Met Maät works with within the client's own systems is only entered into AI systems if this has been agreed with the client in advance.",
            "If you contact Met Maät via LinkedIn, LinkedIn processes your data under its own terms. Government authorities only receive data where required by law."
          ]
        },
        {
          "title": "Transfers outside the EEA",
          "paragraphs": [
            "Microsoft, Google, Anthropic and OpenAI are based in the United States. Where personal data is processed outside the European Economic Area, this is done on the basis of the EU-U.S. Data Privacy Framework or the standard contractual clauses of the European Commission."
          ]
        },
        {
          "title": "Cookies and local storage",
          "paragraphs": [
            "This website does not place tracking or advertising cookies and does not measure visitor statistics. Only two functional preferences are stored in your browser: your language choice and the fact that you have seen the privacy notice.",
            "The fonts on this website are loaded via Google Fonts. In doing so, Google receives your IP address.",
            "Links to other websites, such as LinkedIn, are subject to the privacy rules of those websites."
          ]
        },
        {
          "title": "Security",
          "paragraphs": [
            "Met Maät takes appropriate technical and organizational measures to protect your data against loss or unlawful processing. These include encrypted connections and storage, two-step verification on accounts and access only for those who need it.",
            "In the event of a data breach, Met Maät reports it to the Dutch Data Protection Authority (Autoriteit Persoonsgegevens) where required, and to the people affected if the breach may have serious consequences for them."
          ]
        },
        {
          "title": "Your rights",
          "paragraphs": [
            "You have the right to:"
          ],
          "items": [
            "Access, correct and delete your data, unless a statutory retention obligation applies.",
            "Restrict processing and receive the data you provided yourself in a portable format.",
            "Object to processing based on legitimate interest.",
            "Withdraw consent you have given."
          ],
          "after": [
            "Send your request to info@metmaat.com. Met Maät responds within one month; for a complex request this period may be extended by two months, in which case you will be informed. Requests are free of charge, and Met Maät does not ask for a copy of your identity document.",
            "If you are not satisfied with how Met Maät handles your data, you can file a complaint with the Dutch Data Protection Authority (autoriteitpersoonsgegevens.nl)."
          ]
        },
        {
          "title": "Automated decision-making",
          "paragraphs": [
            "Met Maät does not make decisions about you based solely on automated processing, including with the help of AI."
          ]
        },
        {
          "title": "Changes",
          "paragraphs": [
            "Met Maät updates this statement when its practices change. The version and date of the latest change are shown at the top. Met Maät informs its clients of significant changes."
          ]
        }
      ],
      privacyContact: {
        title: 'Contact',
        text: 'For questions about this privacy statement or the processing of your data, please contact:',
        address1: 'Met Maät',
        address2: 'Attn: Ben Degens',
        address3: 'Penderskoolhofweg 7, 6343RG Klimmen',
        email: 'info@metmaat.com',
      },
      cookieBanner: {
        title: 'Privacy Notice',
        message: 'We don\'t use tracking cookies or collect your data. We only remember your language preference for a better experience.',
        linkText: 'Privacy Statement',
        accept: 'Got it',
      }
    }
  },
  nl: {
    nav: {
      home: 'Home',
      services: 'Diensten',
      methodology: 'Werkwijze',
      about: 'Over',
      contact: 'Contact',
    },
    hero: {
      title: 'Met Maät',
      slogan: 'Software Architecture & Technical Leadership',
      subtitle: 'U kent uw business, wij beheersen softwarearchitectuur en -ontwikkeling. Samen implementeren we de juiste oplossing.',
      readMore: 'Lees verder',
      ctaPrimary: 'Onze Diensten',
      ctaSecondary: 'Kennismaken',
    },
    homeFeatures: {
      title: 'Waarom kiezen voor ons?',
      points: [
        'Wij implementeren altijd een juiste balans tussen noodzakelijke en wenselijke investeringen met focus op efficiëntie, kwaliteit en toekomstbestendigheid.',
        'Wij structureren, innoveren en verhogen kwaliteit en productiviteit in uw softwareontwikkeling en geven richting aan teams.',
        'Alles op basis van uw eisenpakket en onze praktische ervaring.',
      ]
    },
    notFound: {
      title: 'Pagina niet gevonden',
      subtitle: 'Deze pagina bestaat niet (meer).',
      cta: 'Terug naar home',
    },
    experience: {
      title: 'Ervaring',
      subtitle: 'Ruim 14 jaar bouwen en begeleiden van software bij toonaangevende organisaties.',
      caption: 'Expertise opgebouwd bij o.a.:',
    },

    recovery: {
      tag: 'Highlight',
      title: 'Project Recovery',
      desc: 'Heeft u te maken met exploderende cloud-kosten of een team dat vastloopt in technische schuld? Wij analyseren de knelpunten en herstellen de koers met een concreet plan.',
      cta: 'Ontdek hoe we helpen',
    },
    aiSection: {
      title: 'Al kennisgemaakt Met Maät?',
      intro: 'Onze naam gaat over de juiste maat: niet te veel, niet te weinig. Ook knipoogt de naam naar het oude Egypte, waar Maät stond voor orde. Zo kijken wij naar softwareontwikkeling: genoeg structuur om grip te houden, genoeg vaart om waarde te leveren tegen minimale kosten.',
      servicesLead: 'Hoe dat er in de praktijk uitziet:',
      highlights: [
        'Softwarearchitectuur',
        'AI-adoptie in uw ontwikkelproces',
        'Technisch leiderschap & teambegeleiding',
        'Project recovery & architectuur vereenvoudigen',
        'Backend- & clouddevelopment',
        'Kwaliteit, documentatie & overdracht',
      ],
      servicesCta: 'Al onze diensten',
      quote: 'Goede architectuur faciliteert snelheid en zorgt voor blijvende resultaten.',
    },
    footer: {
      desc: 'We brengen structuur, kwaliteit en richting aan teams die software bouwen, hands-on en vanuit ervaring.',
      navTitle: 'Navigatie',
      contactTitle: 'Contact',
      contactText: 'Wilt u meer weten over onze dienstverlening?',
      tagline: 'Balans in een versnellende wereld.',
      rights: 'Alle rechten voorbehouden.',
    },
    company: {
      detailsTitle: 'Bedrijfsgegevens',
      kvkLabel: 'KVK',
      vatLabel: 'BTW',
    },
    partners: {
      kicker: 'Samenwerking & netwerk',
      contact: 'Bespreek uw vraag',
      more: 'Bekijk ons netwerk',
      compact: {
        title: 'Een sterk netwerk',
        description: 'Met Maät werkt samen met ervaren architecten en specialisten voor extra expertise, capaciteit en continuïteit.',
      },
      organisations: {
        softwareLeader: {
          name: 'Software Leader',
          logoAlt: 'Logo van Software Leader',
          contactLabel: 'Bespreek architectuurexpertise via Met Maät',
          specialism: 'Softwarearchitectuur & technisch leiderschap',
          summary: 'Gelijkgestemde architecten die kunnen bijspringen of de architectuurrol overnemen.',
          description: 'Via Software Leader werken we samen met ervaren softwarearchitecten die onze visie op architectuur en technisch leiderschap delen. Zij kunnen ontwerpen toetsen, extra capaciteit bieden of de architectuurrol overnemen wanneer dat nodig is.',
        },
        jellybean: {
          name: 'JellyBean Consultancy',
          logoAlt: 'Logo van JellyBean Consultancy',
          contactLabel: 'Bespreek uw Microsoft 365-vraag via Met Maät',
          specialism: 'Microsoft 365 & Power Platform',
          summary: 'Microsoft 365 en Power Platform: inrichting, automatisering en adoptie.',
          description: 'Voor projecten rond Microsoft 365, SharePoint, Teams en Power Platform werken we samen met JellyBean Consultancy. Van een goed ingerichte digitale werkplek tot het automatiseren van bedrijfsprocessen.',
        },
        modernDay: {
          name: 'Modern Day',
          logoAlt: 'Logo van Modern Day',
          contactLabel: 'Bespreek AI-training en compliance via Met Maät',
          specialism: 'AI-training & compliance',
          summary: 'Praktische AI-training voor teams, met aandacht voor verantwoord gebruik en compliance.',
          description: 'Voor trainingen rond AI, verantwoord gebruik en compliance werken we samen met Modern Day. Praktische begeleiding waarmee mensen en teams AI leren inzetten in hun dagelijkse werk.',
        },
      },
      services: {
        title: 'Vraagt uw project om meer expertise?',
        description: 'Ook voor vragen rond Microsoft 365, AI-training of aanvullende architectuurcapaciteit denken we graag mee. Samen met onze partners bekijken we welke aanpak en expertise bij uw vraag passen.',
        sharedVision: 'Een gedeelde architectuurvisie',
        continuity: 'Daarbij stemmen we de inzet en kennisoverdracht af, met aandacht voor continuïteit zodat het project op dezelfde koers verder kan.',
      },
    },
    services: {
      title: 'Onze',
      titleAccent: 'Diensten',
      subtitle: 'Richting, kwaliteit en efficiëntie in softwareontwikkeling.',
      focusTitle: 'Focusgebieden',
      techTitle: 'Technologieën',
      architecture: {
        title: 'Architecture & Advisory',
        desc: 'We leiden projecten als architect, reviewen bestaande architecturen of adviseren over technische keuzes. Simpel, direct en gericht op resultaat.',
        points: [
          'Software architectuur',
          'Gedetailleerd Ontwerp',
          'Technische besluitvorming',
          'Documentatie Landschap',
          'Kwaliteit & Documentatie',
          'Overdracht & Borging',
        ],
      },
      engineering: {
        title: 'Engineering & AI',
        desc: 'We bouwen software volgens kwaliteitsstandaarden. Daarnaast zitten we bovenop het gebruik van AI in uw bedrijf en tijdens development.',
        points: [
          'Backend & Cloud development',
          'AI Adoptie',
          'DevOps & CI/CD',
        ],
        tech: 'Azure, .NET, C#, ASP.NET, SQL, Entity Framework, REST APIs, Azure DevOps, TypeScript, etc.',
      },
      coaching: {
        title: 'Architect & Team Guidance',
        desc: 'Van een sparring partner voor uw (leiderschaps)team, tot het coachen van tech-leads en architecten tot het geven van een training en het opzetten van een beter ontwikkelproces.',
        points: [
          'Besluitvorming en Efficiëntie',
          'Coaching en Begeleiding',
          'Business Analyse',
          'Ontwikkelproces-optimalisatie',
          'IDesign in de praktijk',
        ],
      },
      recovery: {
        title: 'Project Recovery',
        desc: 'Vastgelopen projecten weer op de rails. We vereenvoudigen de architectuur en brengen de balans terug.',
        points: [
          'Architectuur vereenvoudiging',
          'Cloud cost review',
          'Ontwikkelmethodiek verbeteren',
          'Herstel- & verbeterplan',
          'Stabilisatie & overdracht',
          'Root cause analyse',
          'Andere herstel- & transitievraagstukken',
        ],
      },
    },
    methodology: {
      tag: '',
      title: 'Onze',
      titleAccent: 'Werkwijze',
      subtitle: 'Een gestructureerde engineering-aanpak.',
      clickToEnlarge: 'Klik om te vergroten',
      stepLabel: 'Stap',
      pillars: [
        {
          title: 'Engineering',
          tagline: 'Een engineered proces, geen improvisatie',
          desc: 'Een engineersmindset en een gedisciplineerd proces: ontwerpen, reviewen, bouwen, testen, herhalen. Voorspelbaar en herhaalbaar, met kwaliteit ingebouwd in elke stap.',
        },
        {
          title: 'Eenvoud',
          tagline: 'De eenvoudigste oplossing die werkt',
          desc: 'We brengen complexiteit terug tot de essentie en maken het visueel, zodat het hele team én de business het echt begrijpen.',
        },
        {
          title: 'Toekomstbestendig (AI)',
          tagline: 'Klaar voor wat komt',
          desc: 'Of u de specs, architectuur en ontwerp aan een ontwikkelteam geeft of aan een AI, hoe meer context u meegeeft, hoe beter de output.',
        },
      ],
      pains: {
        kicker: 'Het probleem',
        title: 'Herkenbaar?',
        categoryProject: 'Project & Organisatie',
        categoryTech: 'Code & Techniek',
        categoryTeam: 'Team & Kennis',
        items: [
          'We weten niet precies wanneer het af is.',
          'We hebben geen idee wat dit project ons uiteindelijk gaat kosten.',
          'Onze cloudrekening blijft stijgen en niemand kan uitleggen waarom.',
          'Het werkt wel, maar elke aanpassing duurt eindeloos.',
          'Wat we bouwen matcht niet met de verwachtingen van de business.',
          'Alleen Bob snapt dat deel van de code, en Bob is met vakantie.',
          'We weten van elkaar niet wat we aan het bouwen zijn.',
          'Hoe klaar zijn we voor de toekomst?',
          'Iedereen doet iets met AI. Wij ook?',
        ],
      },
      collab: {
        title: 'U kent uw business, wij kennen softwarearchitectuur.',
        text: 'De meeste van deze problemen ontstaan in de requirements en ontwerp fase, niet in de code. Samen bouwen we software op (en met) maat die bij uw bedrijf past en waarde blijft leveren.',
        cta: 'Laten we kennismaken',
      },
      principlesKicker: 'Onze mindset',
      principlesTitle: 'De mindset die we meebrengen',
      archTitle: 'IDesign-architectuur in de praktijk',
      archDesc: 'Een typisch IDesign-systeem wordt opgedeeld in horizontale lagen: Clients, Logic (Managers en Engines), Access (ResourceAccess) en Resources, ondersteund door een set overkoepelende Utilities zoals Security, Logging en de Service Bus. Deze op volatiliteit gebaseerde structuur houdt verantwoordelijkheden gescheiden en maakt het systeem bestand tegen verandering.',
      archAlt: 'Voorbeeld van een gelaagde IDesign-architectuur met Client-, Logic-, Access- en Resource-lagen en overkoepelende Utilities.',
      archCaption: 'Voorbeeld van een op volatiliteit gebaseerde IDesign-architectuur.',
      idesign: {
        kicker: 'De methodiek',
        title: 'Engineering-methodiek',
        intro: 'Een set aan processen en artefacten die we gebruiken waar het past. Een paar voorbeelden:',
        idesignBadge: 'IDesign',
        blocks: {
          businessAnalysis: {
            label: 'Business Analysis',
            tagline: 'Begrijp het domein',
            desc: 'We brengen de bedrijfsprocessen en definities in kaart volgens internationale UML-standaarden. Precies genoeg documentatie om vast te leggen wat de organisatie echt nodig heeft, niet meer.',
          },
          charterRoadmap: {
            label: 'Charter & Roadmap',
            tagline: 'Project en prioriteiten',
            desc: 'Wat zit er in het project, en wat niet. We brengen de scope, de gerelateerde projecten en features en de bouwvolgorde in kaart, als één simpel visueel overzicht.',
          },
          systemDesign: {
            label: 'System Design',
            tagline: 'Wat bouwen we?',
            desc: 'Op basis van IDesign (Juval Löwy) delen we het systeem op in kleine componenten. Van uw kernbusiness (use cases) naar een heldere, gelaagde architectuur die bestand blijft tegen verandering.',
          },
          projectDesign: {
            label: 'Project Design',
            tagline: 'Hoe gaan we het bouwen?',
            desc: 'Er zijn meerdere wegen naar Rome in een project: goedkoopst, snelst, parallel of via een MVP. We vertalen de architectuur naar een realistisch plan, met doorlooptijd, kosten en het kritieke pad in kaart voordat u gaat uitvoeren.',
          },
          detailedDesign: {
            label: 'Detailed Design',
            tagline: 'Tot aan de contracten',
            desc: 'Interfaces, contracten en andere details per component, samen in een documentatielandschap. Traceerbaar van bedrijfsproces en requirement tot aan het kleinste component, zodat zowel functionele als technische mensen zien waar iets vandaan komt en waarom.',
          },
          execution: {
            label: 'Execution',
            tagline: 'Goed bouwen',
            desc: 'Een softwarefabriek: bouw elk component via dezelfde voorspelbare lifecycle van ontwerp, constructie, review en test.',
          },
        },
        complete: {
          wholeTitle: 'Eén complete methode',
          wholeText: 'Samen vormen de blokken één samenhangende, beproefde methode om software te ontwerpen en op te leveren.',
          modularTitle: 'Of precies het onderdeel dat past',
          modularText: 'Elk blok staat op zichzelf. We zetten in wat past bij uw situatie: maatwerk.',
        },
        captions: {
          businessProcess: 'Analyse van het bedrijfsproces',
          scenarios: 'Uitvoeringsopties: parallel, gefaseerd, MVP',
          projects: 'Roadmap over meerdere projecten',
          useCase: 'Van use case naar call chain',
          arch: 'Een op volatiliteit gebaseerde gelaagde architectuur',
          plan: 'Planning en kritieke-pad-analyse',
          landscape: 'Één samenhangend documentatielandschap',
          serviceLifecycle: 'De service development lifecycle',
          sequenceDiagram: 'Een use case als sequence-diagram',
        },
      },
    },
    about: {
      tag: '',
      title: 'Over',
      titleAccent: '',
      subtitle: 'De persoon achter Met Maät.',
      exp: 'Ben Degens, oprichter Met Maät',
      p1: 'Ben Degens is een ervaren software architect uit Nederland, gespecialiseerd in backend ontwikkeling (.NET C#, Azure) en systeemarchitectuur volgens de IDesign-methodiek.',
      p2: 'In zijn carrière van meer dan 14 jaar heeft hij bijgedragen aan softwareprojecten bij diverse organisaties, van financiële dienstverlening tot medische technologie en van telecom tot industriële productie. Hij heeft ervaring met zowel greenfield projecten als het herstellen van vastgelopen initiatieven, en met werkwijzen zoals IDesign, Scrum, SAFe en Waterfall.',
      p3: 'Voor veel organisaties is de kloof tussen wat nodig is en wat gebouwd wordt een struikelblok. Ben\'s aanpak begint met actief luisteren en het begrijpen van de unieke werkwijze van elke organisatie. Die basis gebruikt hij om requirements helder te krijgen, systemen te ontwerpen en complexiteit te vertalen naar behapbare componenten.',
      p4: 'Hij is ervan overtuigd dat het succes van zijn klanten ook zijn eigen succes is. U mag dan ook verwachten dat hij proactief met u meedenkt (én meedoet!), om zo maximaal bij te dragen aan het succes van uw organisatie.',
      location: 'Nederland',
      languages: 'Dutch & English',
      certification: 'Azure, .NET, C#',
      tags: ['System Architecture', 'DevOps & CI/CD', 'Microservices', 'API (management)', 'Databases, (No)SQL', 'AI & Data', 'UML, Diagrams, Docs', 'Project, Charter, Roadmap', 'Project Recovery', 'Technical Leadership', 'IDesign'],
      expertiseTitle: 'Expertise & Ervaring',
      skill1: {
        title: 'Opleiding & Methode',
        desc: 'Opgeleid via IDesign\'s Architects Master Class en Project Design Master Class, diepgaande cursussen in plaats van snelle certificaten, plus jarenlange praktijkervaring in IDesign-minded omgevingen.',
      },
      skill2: {
        title: 'Technologie & AI',
        desc: 'We hebben diepgaande kennis van C#, .NET, Azure, cloud en backend-ontwikkeling. We kiezen voor simpele technologie die werkt boven hype-gedreven trends. Maar we kijken niet weg van wat nieuw is: we zetten AI in waar we kunnen om sneller te werken zonder het overzicht te verliezen.',
      },
      skill3: {
        title: 'Leiderschap',
        desc: 'Ruime ervaring in zowel startups als grote organisaties, met Agile, Scrum, Waterfall en SAFe. Focus op het versterken van teams, technisch leiderschap en het creëren van werkbare processen waarin kwaliteit en snelheid samen kunnen bestaan.',
      },
      ctaTitle: 'Kennismaken',
      ctaDesc: 'Heeft u een softwareproject dat vastloopt, of wilt u juist een sterke start maken? Neem direct contact op met Ben.',
      emailBtn: 'E-mail sturen',
    },
    contact: {
      tag: '',
      title: 'Laten we',
      titleAccent: 'Kennismaken',
      subtitle: 'Van sparren over een idee tot een project op gang brengen: we denken graag mee.',
      infoTitle: 'Contactgegevens',
      emailLabel: 'E-mail',
      emailValue: 'info@metmaat.com',
      mobileLabel: 'Mobiel',
      call: 'Bellen',
      whatsapp: 'WhatsApp',
      linkedinLabel: 'LinkedIn',
      contactInformation: 'Contactinformatie',
      description: 'Neem vrijblijvend contact met ons op voor meer informatie of vragen over onze diensten.',
      whoIsBen: 'Wie is Ben?',
    },
    legal: {
      disclaimerTitle: 'Disclaimer',
      disclaimerSubtitle: 'Juridische voorwaarden en condities met betrekking tot het gebruik van deze website.',
      privacyTitle: 'Privacyverklaring',
      disclaimerText: "Met Maät besteedt grote zorg aan de juistheid en actualiteit van de inhoud van deze website. Met Maät aanvaardt echter geen aansprakelijkheid voor de volledigheid en juistheid van de informatie op deze website, noch voor de inhoud van websites waarnaar wordt gelinkt. Met Maät kan de informatie op deze website op elk moment en zonder voorafgaande aankondiging wijzigen. Dit geldt ook voor de beschreven dienstverlening.\n\nDe op deze website beschreven diensten, werkwijzen en voorbeelden zijn indicatief en vormen geen aanbod. Verplichtingen van Met Maät beperken zich tot wat in een overeenkomst is vastgelegd. Daarop zijn de algemene voorwaarden van Met Maät van toepassing, die met de offerte worden meegestuurd.\n\nMet Maät gebruikt AI-systemen als hulpmiddel bij het opstellen van teksten en andere inhoud, waaronder de inhoud van deze website. Alle inhoud wordt vóór publicatie door Met Maät gecontroleerd; Met Maät blijft daarvoor verantwoordelijk.\n\nDe intellectuele eigendomsrechten op de inhoud van deze website berusten bij Met Maät, met uitzondering van logo's en merken van derden. Het is niet toegestaan de website of delen daarvan te publiceren of te kopiëren zonder uitdrukkelijke schriftelijke toestemming van Met Maät.\n\nU bent zelf verantwoordelijk voor het virusvrij houden van de applicaties waarmee u deze website bezoekt. Voor zover wettelijk toegestaan is Met Maät niet aansprakelijk voor directe of indirecte schade die verband houdt met het gebruik van deze website of een gelinkte website, in het bijzonder niet voor onderbrekingen of verlies van programma's of gegevens in uw IT-systemen.",
      privacyVersion: 'Versie 1.1, oktober 2026',
      privacyGeneral: 'De besloten vennootschap Met Maät B.V. (hierna te noemen Met Maät) begrijpt het belang van privacy en een zorgvuldige omgang met persoonsgegevens. Als specialist in software architectuur, kwaliteitsstructuur en project recovery hanteert Met Maät een hoge standaard voor informatiebeveiliging en transparantie, zowel in haar technische oplossingen als in haar eigen bedrijfsvoering.',
      privacySections: [
        {
          "title": "Wie is verantwoordelijk?",
          "paragraphs": [
            "Met Maät B.V., gevestigd aan Penderskoolhofweg 7, 6343RG Klimmen (KvK 42138772), is verwerkingsverantwoordelijke voor de gegevens die nodig zijn voor haar eigen bedrijfsvoering en administratie. Met Maät heeft geen functionaris voor gegevensbescherming; vragen kunt u rechtstreeks stellen via info@metmaat.com.",
            "Werkt Met Maät als consultant of architect binnen de systemen van een opdrachtgever, dan is de opdrachtgever verwerkingsverantwoordelijke voor de gegevens die daar worden verwerkt. Met Maät is dan verwerker en legt dat waar nodig vast in een verwerkersovereenkomst."
          ]
        },
        {
          "title": "Welke gegevens verwerkt Met Maät?",
          "paragraphs": [
            "Met Maät verwerkt uitsluitend gegevens die nodig zijn voor een professionele samenwerking:"
          ],
          "items": [
            "Contact via e-mail, telefoon of LinkedIn: uw naam, contactgegevens en de inhoud van uw bericht.",
            "Samenwerking: naam, functie, bedrijf, zakelijke contactgegevens en correspondentie over het project, zoals architectuurkeuzes, documentatie en offertes.",
            "Facturatie: bedrijfsnaam en adres, KvK-nummer, btw-nummer, contactpersoon en bankgegevens.",
            "Technische gegevens: uw IP-adres, dat nodig is om de website te tonen en door de hostingpartij kortstondig wordt gelogd."
          ],
          "after": [
            "Met Maät vraagt niet om bijzondere persoonsgegevens, zoals gegevens over gezondheid of religie. Deze website heeft geen contactformulier."
          ]
        },
        {
          "title": "Doelen en grondslagen",
          "items": [
            "Beantwoorden van vragen en plannen van een kennismaking: gerechtvaardigd belang.",
            "Opstellen van offertes en uitvoeren van opdrachten: uitvoering van de overeenkomst.",
            "Facturatie en administratie: wettelijke verplichting (fiscale bewaarplicht).",
            "Beveiliging en goede werking van de website: gerechtvaardigd belang.",
            "Vermelden van uw naam of bedrijf als referentie of in een case study: toestemming, die u altijd kunt intrekken."
          ],
          "after": [
            "Met Maät verkoopt geen gegevens, verstuurt geen nieuwsbrieven of advertenties en maakt geen profielen."
          ]
        },
        {
          "title": "Bewaartermijnen",
          "items": [
            "Vragen die niet tot een samenwerking leiden: 12 maanden na het laatste contact.",
            "Correspondentie met opdrachtgevers: tijdens de samenwerking en tot 2 jaar daarna.",
            "Offertes en overeenkomsten: tijdens de samenwerking en tot 5 jaar daarna (de wettelijke verjaringstermijn).",
            "Facturen en administratie: 7 jaar, conform de fiscale bewaarplicht.",
            "Toestemming voor een referentie: tot u die intrekt."
          ]
        },
        {
          "title": "Ontvangers en verwerkers",
          "paragraphs": [
            "Met Maät deelt gegevens alleen als dat nodig is voor de dienstverlening of als de wet dat verplicht. De volgende partijen ontvangen of verwerken gegevens in opdracht van Met Maät:"
          ],
          "items": [
            "Microsoft (Microsoft 365): e-mail en documentopslag.",
            "Google (Firebase): hosting.",
            "Anthropic (Claude), OpenAI (ChatGPT / Codex) en Google (Gemini): AI-assistenten.",
            "Boekhouder: verwerking van de financiële administratie en aangiftes.",
            "Freelancers en specialisten die Met Maät voor een opdracht inschakelt: alleen de gegevens die zij voor die opdracht nodig hebben, onder geheimhouding."
          ],
          "after": [
            "Deze partijen verwerken gegevens uitsluitend ten behoeve van de dienstverlening aan Met Maät en zijn gebonden aan geheimhouding en beveiliging. Met Maät heeft de AI-systemen zo ingesteld dat ingevoerde gegevens niet worden gebruikt om hun modellen te trainen.",
            "Gegevens van opdrachtgevers waarmee Met Maät binnen de systemen van de opdrachtgever werkt, worden alleen in AI-systemen ingevoerd als dat vooraf met de opdrachtgever is afgesproken.",
            "Neemt u contact op via LinkedIn, dan verwerkt LinkedIn uw gegevens onder haar eigen voorwaarden. Overheidsinstanties ontvangen gegevens alleen als de wet dat verplicht."
          ]
        },
        {
          "title": "Doorgifte buiten de EER",
          "paragraphs": [
            "Microsoft, Google, Anthropic en OpenAI zijn gevestigd in de Verenigde Staten. Waar persoonsgegevens buiten de Europese Economische Ruimte worden verwerkt, gebeurt dat op basis van het EU-VS Data Privacy Framework of de standaardcontractbepalingen van de Europese Commissie."
          ]
        },
        {
          "title": "Cookies en lokale opslag",
          "paragraphs": [
            "Deze website plaatst geen tracking- of advertentiecookies en meet geen bezoekersstatistieken. In uw browser worden alleen twee functionele voorkeuren opgeslagen: uw taalkeuze en dat u de privacymelding heeft gezien.",
            "De lettertypen van deze website worden geladen via Google Fonts. Daarbij ontvangt Google uw IP-adres.",
            "Links naar andere websites, zoals LinkedIn, vallen onder de privacyregels van die websites."
          ]
        },
        {
          "title": "Beveiliging",
          "paragraphs": [
            "Met Maät neemt passende technische en organisatorische maatregelen om uw gegevens te beschermen tegen verlies of onrechtmatige verwerking. Daaronder vallen versleutelde verbindingen en opslag, tweestapsverificatie op accounts en toegang alleen voor wie die nodig heeft.",
            "Bij een datalek meldt Met Maät dat, waar verplicht, aan de Autoriteit Persoonsgegevens, en aan betrokkenen als het lek ernstige gevolgen voor hen kan hebben."
          ]
        },
        {
          "title": "Uw rechten",
          "paragraphs": [
            "U heeft het recht op:"
          ],
          "items": [
            "Inzage, correctie en verwijdering van uw gegevens, voor zover geen wettelijke bewaarplicht geldt.",
            "Beperking van de verwerking en overdraagbaarheid van gegevens die u zelf heeft verstrekt.",
            "Bezwaar tegen verwerking op basis van gerechtvaardigd belang.",
            "Intrekken van een gegeven toestemming."
          ],
          "after": [
            "Stuur uw verzoek naar info@metmaat.com. Met Maät reageert binnen een maand; bij een complex verzoek kan die termijn met twee maanden worden verlengd, waarover u dan bericht krijgt. Een verzoek is kosteloos en Met Maät vraagt daarbij niet om een kopie van uw identiteitsbewijs.",
            "Bent u niet tevreden over hoe Met Maät met uw gegevens omgaat, dan kunt u een klacht indienen bij de Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl)."
          ]
        },
        {
          "title": "Geautomatiseerde besluitvorming",
          "paragraphs": [
            "Met Maät neemt geen besluiten over u die uitsluitend op geautomatiseerde verwerking berusten, ook niet met behulp van AI."
          ]
        },
        {
          "title": "Wijzigingen",
          "paragraphs": [
            "Met Maät past deze verklaring aan wanneer haar werkwijze verandert. Bovenaan staat de versie en datum van de laatste wijziging. Belangrijke wijzigingen meldt Met Maät aan haar opdrachtgevers."
          ]
        }
      ],
      privacyContact: {
        title: 'Contact',
        text: 'Voor vragen over deze privacyverklaring of de verwerking van uw gegevens kunt u contact opnemen met:',
        address1: 'Met Maät',
        address2: 'T.a.v. Ben Degens',
        address3: 'Penderskoolhofweg 7, 6343RG Klimmen',
        email: 'info@metmaat.com',
      },
      cookieBanner: {
        title: 'Privacy Melding',
        message: 'Wij verzamelen geen gegevens en gebruiken geen tracking cookies. Alleen uw taalvoorkeur wordt onthouden voor een optimale ervaring.',
        linkText: 'Privacyverklaring',
        accept: 'Begrepen',
      }
    }
  }
};
